import type {
  RetryInvoiceExportAttemptMutationOptions,
  RetryInvoiceExportAttemptVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../../queryKeys';

import { retryInvoiceExportAttempt } from './retryInvoiceExportAttempt';

/**
 * Retries a failed invoice export attempt and refreshes related queries.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns Mutation object to trigger the retry.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/retry_export_attempt_invoices__invoice_id__export_attempts__attempt_id__retry_post
 */
export function useMutationRetryInvoiceExportAttempt(
  options?: RetryInvoiceExportAttemptMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attemptId,
      invoiceId,
    }: RetryInvoiceExportAttemptVariables) =>
      retryInvoiceExportAttempt(invoiceId, attemptId),
    ...options,
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getDetail(variables.invoiceId),
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getExportAttempt(
            variables.invoiceId,
            variables.attemptId,
          ),
          refetchType: 'all',
        }),
      ]);

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
}
